import { mdiAlertCircleOutline, mdiCheck, mdiTrayArrowDown } from "@mdi/js";
import { Alert, Box, Button, Grid, Menu, MenuItem, Snackbar } from "@mui/material";
import styles from "./reportSection.module.scss";
import { containerizedDetailService, inventoryService } from "../../api/services";
import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { useClient } from "../../utils/ClientContext";
import ReportDialog from "./ReportDialog/ReportDialog";
import Loader from "../Loader/Loader";
interface InventoryItem {
  inventoryId: number;
  inventoryName: string;
  inventoryNumber: number;
  isContainerized: number;
}
const ReportsSection: React.FC = () =>{
    //#region Declare
    const [inventoryData, setInventoryData] = React.useState<InventoryItem[]>([]);
    const [filteredInventoryData, setFilteredInventoryData] = React.useState<InventoryItem[]>([]);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [severity, setSeverity] = useState<"success" | "error">("success");
    const [currentReport, setCurrentReport] = useState<"tag"|"itemDetails" | "valuationReport"|null>();
    const [currentReportTitle, setCurrentReportTitle] = useState<string|null>();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [openTagDialog, setOpenTagDialog] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const { client } = useClient();
    const clientId = client.id;
    //#endregion

    //#region React Hooks
    useEffect(()=>{
        getClientInventoriesByClient(clientId);
    },[clientId]);
    //#endregion

    //#region Common Functions
    const getIcon = () => {
    if (severity === "success") {
        return <Icon path={mdiCheck} size={1} />; // Custom success icon
    }
    return <Icon path={mdiAlertCircleOutline} size={1} />; // Custom error icon
    };
    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };
    //#endregion
    //#region Important functions
    const getClientInventoriesByClient = async (clientId: number) => {
    try {
      const response =
        await inventoryService.getClientInventoriesByClientIdAsync();
      if (response.isSuccess) {
        if (
          response.clientInventoriesResultViewModel &&
          response.clientInventoriesResultViewModel.length > 0
        ) {
          const clientInventoriesResultViewModel =
            response.clientInventoriesResultViewModel.map((inventory: any) => {
              return {
                inventoryId: inventory.id,
                inventoryName: inventory.name,
                inventoryNumber: inventory.inventoryNumber,
                isContainerized: inventory.isContainerized
              };
            });
          setInventoryData(clientInventoriesResultViewModel);
        }
      }
    } catch (error: any) {
      console.error("Error:", error);
    }
    };
    const handleInventoryItemClick = async (inventoryId: number) => {
      setShowLoader(true);
        try {
        const response = await inventoryService.exportTagsToExcelAsync(
            inventoryId
        );
        if (response.isSuccess) {
            const base64Data = response.excelData;
    
            if (!base64Data) {
            setSeverity("error");
            setSnackbarMessage("No data received from server.");
            setOpenSnackbar(true);
            }
            exportDownloadExcel(base64Data, `ICM_Tags_${inventoryId}`);
        } else {
            const errorMessage =
            response.errors.join(", ") || "Failed to download Excel file.";
            setSeverity("error");
            setSnackbarMessage(errorMessage);
            setOpenSnackbar(true);
        }
        } catch (error: any) {
        console.error("Error downloading Excel file:", error);
        setSeverity("error");
        setSnackbarMessage("An error occurred while downloading the Excel file.");
        setOpenSnackbar(true);
        }
        finally{
        setShowLoader(false);
        }
    };
    const handleExportContainerizedDetailsClick = async (inventoryId: number) => {
        setShowLoader(true);
        try {
        const response = await containerizedDetailService.exportContainerizedDetails(
            inventoryId
        );
        if (response.isSuccess) {
            const base64Data = response.excelData;
    
            if (!base64Data) {
            setSeverity("error");
            setSnackbarMessage("No data received from server.");
            setOpenSnackbar(true);
            }
            exportDownloadExcel(base64Data, `ICM_ItemDetails_${inventoryId}`);
        } else {
            const errorMessage =
            response.errors.join(", ") || "Failed to download Excel file.";
            setSeverity("error");
            setSnackbarMessage(errorMessage);
            setOpenSnackbar(true);
        }
        } catch (error: any) {
        console.error("Error downloading Excel file:", error);
        setSeverity("error");
        setSnackbarMessage("An error occurred while downloading the Excel file.");
        setOpenSnackbar(true);
        }
        finally{
        setShowLoader(false);
        }
    };
    const exportDownloadExcel= async (base64Data:any, fileName:string)=>{
        const binaryString = window.atob(base64Data);
    
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
            }
    
            const blob = new Blob([bytes.buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
    
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${fileName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    
            setSeverity("success");
            setSnackbarMessage("Excel file downloaded successfully.");
            setOpenSnackbar(true);
                setTimeout(() => {
            handleCloseSnackbar();
        }, 5000);
    
    }
    const configureOpenDialogue=(reportName:"tag"|"itemDetails"|"valuationReport")=>{
      setCurrentReport(reportName);
      if(reportName === "tag"){
          setFilteredInventoryData(inventoryData);
          setCurrentReportTitle("Tag Report Download");
        }
        else if(reportName === "itemDetails"){
          setFilteredInventoryData(inventoryData.filter(x=>x.isContainerized));
          setCurrentReportTitle("Item Details Report Download");
        }
        else if(reportName === "valuationReport"){
          setFilteredInventoryData(inventoryData);
          setCurrentReportTitle("Valuation Report Download");
        }
      setOpenTagDialog(true);
    }
    const configureDownloadDialogue=(inventoryId: number)=>{
      if(currentReport){
        if(currentReport === "tag"){
          handleInventoryItemClick(inventoryId);
        }
        else if(currentReport === "itemDetails"){
          handleExportContainerizedDetailsClick(inventoryId);
        }
        else if(currentReport === "valuationReport"){
          handleInventoryItemClick(inventoryId);
        }
      } 
      handleDialogueClose();     
    }
    const handleDialogueClose=()=>{
      setFilteredInventoryData([]);
      setCurrentReport(null);
      setCurrentReportTitle(null);
      setOpenTagDialog(false);
    }
    //#endregion
return (
  <>
    {" "}
    {inventoryData && inventoryData.length > 0 && (
    <Grid
      container
      size={{ sm: 12 }}
      className={`${styles.counter_container} ${styles.reports_section}`}
      spacing={2}
    >
      <Grid size={{ sm: 4, xs: 12 }} className={styles.counter_box}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Tag Report</span>
          <Button variant="outlined" sx={{ border: "none" }} onClick={() => configureOpenDialogue("tag")}>
            <Icon path={mdiTrayArrowDown} size={0.8} />
          </Button>
        </Box>
      </Grid>
      {inventoryData.filter(x=>x.isContainerized).length > 0 && (
        <Grid size={{ sm: 4, xs: 12 }} className={styles.counter_box}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Item Details Report</span>
          
                <Button variant="outlined" sx={{ border: "none" }} onClick={() => configureOpenDialogue("itemDetails")}>
  <Icon path={mdiTrayArrowDown} size={0.8} />
</Button>
        </Box>
      </Grid>
      )}
    </Grid>
    )}
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      open={openSnackbar}
      autoHideDuration={10000}
    >
      <Alert
        onClose={handleCloseSnackbar}
        severity={severity}
        icon={getIcon()}
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    
      {showLoader && <Loader/>}
    {openTagDialog && (<ReportDialog
  open={openTagDialog}
  onClose={handleDialogueClose}
  inventories={filteredInventoryData}
  onDownload={configureDownloadDialogue}
  title={currentReportTitle??null}
/>)}

  </>
);
};
export default ReportsSection;